import { useState, useEffect } from 'react';

const HeroSection = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <>
      {/* ── FULL GRADIENT WRAPPER (navbar + hero) ── */}
      <div
        className="relative w-full"
        style={{
          background: 'linear-gradient(135deg, #002060 0%, #003580 50%, #1a8abf 100%)',
        }}
      >
        {/* ── NAVBAR ── */}
        <nav
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            scrolled
              ? 'bg-white shadow-sm'
              : 'bg-transparent'
          }`}
        >
          <div className="w-full max-w-7xl mx-auto px-6 py-3 flex items-center justify-between relative">
            {/* Left: small logo */}
            <div className="flex-1 flex items-center">
              <div
                className="cursor-pointer"
                onClick={() => scrollTo('hero')}
              >
                <img
                  src="https://public.readdy.ai/ai/img_res/784e597e-453e-4120-a0b9-90af86a62c15.png"
                  alt="Polar Clim"
                  className="h-14 w-auto object-contain"
                />
              </div>
            </div>

            {/* Center: nav links — desktop */}
            <div className="hidden md:flex items-center gap-7">
              {[
                ['services', 'Services'],
                ['engagements', 'Engagements'],
                ['apropos', 'À Propos'],
                ['temoignages', 'Témoignages'],
                ['contact', 'Contact'],
              ].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className="text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors hover:opacity-70"
                  style={{ color: scrolled ? '#1a3a60' : '#ffffff' }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Right: spacer + hamburger mobile */}
            <div className="flex-1 flex items-center justify-end">
              <button
                className="md:hidden cursor-pointer z-10"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Menu"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i
                    className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl`}
                    style={{ color: scrolled ? '#003a7a' : '#ffffff' }}
                  />
                </div>
              </button>
            </div>
          </div>

          {/* Dropdown menu */}
          {menuOpen && (
            <div className="bg-white border-t px-6 py-4 flex flex-col gap-1" style={{ borderColor: '#e5f0fb' }}>
              {[
                ['services', 'Services'],
                ['engagements', 'Engagements'],
                ['apropos', 'À Propos'],
                ['temoignages', 'Témoignages'],
                ['contact', 'Contact'],
              ].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className="text-left font-semibold py-2 border-b cursor-pointer transition-opacity hover:opacity-60 text-sm"
                  style={{ color: '#1a3a60', borderColor: '#f0f4f8' }}
                >
                  {label}
                </button>
              ))}
              <a
                href="tel:0651341160"
                className="mt-2 flex items-center gap-2 text-sm font-bold"
                style={{ color: '#004f9a' }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill" />
                </div>
                06 51 34 11 60
              </a>
            </div>
          )}
        </nav>

        {/* ── HERO ── */}
        <section
          id="hero"
          className="relative w-full flex flex-col items-center justify-center overflow-hidden"
          style={{ minHeight: '100vh', paddingTop: '100px', paddingBottom: '0' }}
        >
          {/* Subtle radial highlight */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse 70% 50% at 50% 35%, rgba(255,255,255,0.10) 0%, transparent 70%)',
            }}
          />

          {/* Text content */}
          <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-2xl mx-auto w-full pb-16">

            {/* Big logo in hero */}
            <div className="mb-8 mt-2">
              <img
                src="https://public.readdy.ai/ai/img_res/784e597e-453e-4120-a0b9-90af86a62c15.png"
                alt="Polar Clim"
                className="h-40 w-auto object-contain"
              />
            </div>

            {/* Label */}
            <div className="flex items-center gap-3 mb-6 mt-2">
              <div className="h-px w-8 bg-white/50" />
              <span className="text-white/90 text-xs font-bold uppercase tracking-widest">
                Experts en Climatisation
              </span>
              <div className="h-px w-8 bg-white/50" />
            </div>

            {/* Title */}
            <h1
              className="text-5xl lg:text-6xl font-black text-white leading-tight mb-2"
              style={{ textShadow: '0 2px 18px rgba(0,0,0,0.35)' }}
            >
              La température Maîtrisée
            </h1>
            <p
              className="text-4xl lg:text-5xl font-black leading-tight mb-7"
              style={{
                color: '#7dd8f8',
                textShadow: '0 2px 18px rgba(0,0,0,0.35)',
              }}
            >
              Le Confort Assuré
            </p>

            {/* Description */}
            <p className="text-white/85 text-sm lg:text-base leading-relaxed mb-10 max-w-xl">
              Votre spécialiste certifié en climatisation et pompes à chaleur à Nice et dans les alentours. Confort, performance et qualité.
            </p>

            {/* CTA buttons */}
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => scrollTo('contact')}
                className="flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-white text-sm cursor-pointer whitespace-nowrap transition-opacity hover:opacity-80"
                style={{ background: 'rgba(10,20,50,0.55)' }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-check-line" />
                </div>
                Prendre Rendez-vous
              </button>
              <button
                onClick={() => scrollTo('services')}
                className="flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-white text-sm cursor-pointer whitespace-nowrap transition-all hover:bg-white/20"
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '1.5px solid rgba(255,255,255,0.5)',
                }}
              >
                Nos Services
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </div>
              </button>
            </div>
          </div>

          {/* Stats bar — pinned at bottom */}
          <div
            className="relative z-10 w-full mt-auto"
            style={{
              background: 'rgba(255,255,255,0.13)',
              borderTop: '1px solid rgba(255,255,255,0.22)',
            }}
          >
            <div className="max-w-3xl mx-auto px-6 py-6 flex flex-wrap justify-center divide-x divide-white/25">
              {[
                { value: '100%', label: 'CLIENTS SATISFAITS' },
                { value: '+20', label: "ANS D'EXPÉRIENCE" },
                { value: '5 ans', label: 'GARANTIE COMPRESSEUR' },
              ].map((stat) => (
                <div key={stat.label} className="flex-1 min-w-[140px] text-center px-6 py-2">
                  <p className="text-white text-2xl font-black leading-tight">{stat.value}</p>
                  <p className="text-white/70 text-xs font-semibold mt-1 tracking-wider">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HeroSection;
